// src/admin/screens/borrowers/tabs/KycTab.tsx
// Shows every KYC submission for this borrower. Each submission links
// to the full reviewer screen (built in Task 62) so the agent can view
// signed NRC photos and approve/reject without losing place.
//
// The schema stores OCR output in `ocr_result_jsonb` (NOT a direct
// `nrc_number` column on the submission). We pull nrc_number out of
// that JSON for display here; the reviewer screen will show the full
// OCR payload with field-by-field comparison.
import { Link } from 'react-router-dom';
import Card from '@admin/components/Card';
import StatusChip from '@admin/components/StatusChip';
import { formatCatDateTime } from '@admin/utils/dates';
import type { BorrowerDetail } from '@admin/hooks/useBorrower';
import type { Database } from '@/lib/database.types';

type KycStatus = Database['public']['Enums']['kyc_status'];
const TONE: Record<KycStatus, 'info' | 'positive' | 'warn' | 'danger'> = {
  pending: 'info',
  verified: 'positive',
  manual_review: 'warn',
  rejected: 'danger',
};
const LABEL: Record<KycStatus, string> = {
  pending: 'Pending',
  verified: 'Verified',
  manual_review: 'Manual review',
  rejected: 'Rejected',
};

// Safely pluck a string field from jsonb without trusting its shape.
function nrcFromOcr(ocr: unknown): string | null {
  if (!ocr || typeof ocr !== 'object') return null;
  const v = (ocr as Record<string, unknown>)['nrc_number'];
  return typeof v === 'string' ? v : null;
}

export default function KycTab({ borrower }: { borrower: BorrowerDetail }) {
  const subs = [...(borrower.kyc_submissions ?? [])].sort((a, b) =>
    a.submitted_at < b.submitted_at ? 1 : -1,
  );
  if (subs.length === 0) {
    return (
      <Card title="KYC">
        <p className="small">No KYC submissions yet.</p>
      </Card>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {subs.map((s) => (
        <Card
          key={s.id}
          title={<span>Submitted {formatCatDateTime(s.submitted_at)}</span>}
          actions={<Link to={`/kyc/${s.id}`}>Review →</Link>}
        >
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: '6px 14px',
              margin: 0,
            }}
          >
            <dt>Status</dt>
            <dd>
              <StatusChip label={LABEL[s.status]} tone={TONE[s.status]} />
            </dd>
            <dt>NRC (OCR)</dt>
            <dd>{nrcFromOcr(s.ocr_result_jsonb) ?? '—'}</dd>
            <dt>Reviewed</dt>
            <dd>{formatCatDateTime(s.reviewed_at)}</dd>
          </dl>
        </Card>
      ))}
    </div>
  );
}
